import React, { useState, useCallback, useRef, useEffect } from 'react';
import L from 'leaflet';
import type { POI } from '../types';
import {
  getMarkerStyle,
  getGoogleMapsUrl,
  getSourceUrl,
  getTierLabel,
  sortSourcesByRank,
  TYPE_LABELS,
  TYPE_STYLES,
} from '../constants';
import { GUIDES } from '../data/guides';
import { GeminiAssistant } from './GeminiAssistant';

interface POIPopupProps {
  poi: POI;
  isLocked?: boolean;
  onUnlockClick?: () => void;
}

const LinkIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" /></svg>
);

const ShareIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
  </svg>
);

const PhoneIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.128a11.042 11.042 0 005.516 5.516l1.128-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

/**
 * Normalizza un numero di telefono per uso in href="tel:..."
 * - Rimuove tutti gli spazi e i caratteri non numerici eccetto il "+" iniziale
 * - Apple iOS/Android accettano spazi ma e' piu' sicuro toglierli
 */
function telHref(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, "");
  return `tel:${cleaned}`;
}

export const POIPopup: React.FC<POIPopupProps> = ({ poi, isLocked, onUnlockClick }) => {
  const [showAssistant, setShowAssistant] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Impedisce che i click/scroll dentro al popup si propaghino alla mappa
  useEffect(() => {
    if (containerRef.current) {
      L.DomEvent.disableClickPropagation(containerRef.current);
      L.DomEvent.disableScrollPropagation(containerRef.current);
    }
  }, []);

  const markerStyle = getMarkerStyle(poi);
  const Icon = markerStyle.icon;
  const typeLabel = TYPE_LABELS[poi.type];
  const typeColor = TYPE_STYLES[poi.type].color;
  const sortedSources = sortSourcesByRank(poi.sources);
  const googleMapsUrl = getGoogleMapsUrl(poi);

  // === LOCKED VIEW ===
  // POI di una regione non sbloccata: mostra solo header (nome + tipo + città)
  // e un CTA che apre il paywall. Niente teaser delle fonti, niente address,
  // niente sito/telefono/Google Maps (opzione B della decisione di prodotto).
  if (isLocked) {
    return (
      <div ref={containerRef} className="text-gray-900 font-sans">
        <div className="flex items-start mb-3">
          <div className={`flex-shrink-0 grid place-items-center w-10 h-10 rounded-full mr-3 ${markerStyle.color} opacity-60`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-grow min-w-0">
            <h3 className="font-bold text-lg leading-tight">{poi.name}</h3>
            <div className="flex flex-wrap gap-1 mt-1 items-center">
              <span className={`text-xs font-semibold text-white px-2 py-0.5 rounded-full ${typeColor}`}>
                {typeLabel}
              </span>
              {poi.city && <span className="text-xs text-gray-500">{poi.city}{poi.region ? `, ${poi.region}` : ""}</span>}
            </div>
          </div>
        </div>

        {/* Locked overlay con CTA */}
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-lg p-4 text-center">
          <div className="flex justify-center mb-2">
            <svg className="w-8 h-8 text-cyan-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-cyan-900 mb-3">
            Questo locale e' in {poi.region || "un'altra regione"}
          </p>
          <p className="text-xs text-cyan-800 mb-4">
            Sblocca tutte le 20 regioni d'Italia con un acquisto unico.
          </p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onUnlockClick?.(); }}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 px-4 rounded-lg transition-colors shadow-sm"
          >
            Sblocca tutto — €7,99
          </button>
        </div>
      </div>
    );
  }
  // === END LOCKED VIEW ===

  const handleShare = useCallback(async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?poi=${poi.id}`;
    const shareData = {
      title: poi.name,
      text: `Dai un'occhiata a ${poi.name} su Itinere — tutte le guide in una sola mappa!`,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error("Share failed:", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        alert("Could not copy link to clipboard.");
      }
    }
  }, [poi]);

  return (
    <div ref={containerRef} className="text-gray-900 font-sans">
      {/* Header */}
      <div className="flex items-start mb-2">
        <div className={`flex-shrink-0 grid place-items-center w-10 h-10 rounded-full mr-3 ${markerStyle.color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-grow min-w-0">
          <h3 className="font-bold text-lg leading-tight">{poi.name}</h3>
          <div className="flex flex-wrap gap-1 mt-1">
            <span className={`text-xs font-semibold text-white px-2 py-0.5 rounded-full ${typeColor}`}>
              {typeLabel}
            </span>
            {poi.extraTypes?.map(t => (
              <span key={t} className={`text-xs font-semibold text-white px-2 py-0.5 rounded-full ${TYPE_STYLES[t].color}`}>
                {TYPE_LABELS[t]}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Share */}
      <div className="mb-3">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleShare(); }}
          className="text-cyan-600 hover:text-cyan-500 transition-colors font-semibold flex items-center text-sm"
        >
            <ShareIcon /> {copied ? 'Copiato!' : 'Condividi'}
        </button>
      </div>

      {/* Address */}
      <p className="text-xs text-gray-500 mb-3">{poi.address}</p>

      {/* External: website + Google Maps page + Telefono (cliccabile per chiamare) */}
      <div className="flex items-center gap-4 mb-3 text-sm flex-wrap">
        {poi.websiteUrl && (
          <a href={poi.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 transition-colors font-semibold">Sito web<LinkIcon/></a>
        )}
        <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 transition-colors font-semibold">Pagina Google Maps<LinkIcon/></a>
        {poi.phone && (
          <a href={telHref(poi.phone)} className="text-cyan-600 hover:text-cyan-500 transition-colors font-semibold">
            {poi.phone}<PhoneIcon/>
          </a>
        )}
      </div>

      {/* Sources */}
      {sortedSources.length > 0 && (
        <div className="border-t border-gray-200 pt-3 mb-3">
          <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Segnalato da</h4>
          <ul className="space-y-2">
            {sortedSources.map((source, idx) => {
              const guide = GUIDES[source.guideId];
              if (!guide) return null;
              const tierLabel = getTierLabel(source);
              const guideUrl = getSourceUrl(source);
              return (
                <li key={idx} className="text-sm">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">
                      {guide.name}{guide.edition ? ` ${guide.edition}` : ""}
                    </span>
                    {tierLabel && (
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                        {tierLabel}
                      </span>
                    )}
                    {source.score && (
                      <span className="text-xs text-gray-500">Voto {source.score}/100</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-xs">
                    <a href={guideUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 font-semibold">
                      Vedi sulla guida<LinkIcon/>
                    </a>
                    {guide.amazonUrl && (
                      <a
                        href={guide.amazonUrl}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        className="text-amber-700 hover:text-amber-600 font-semibold"
                        title="Link affiliato Amazon"
                      >
                        Compra la guida<LinkIcon/>
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* AI Assistant */}
      {!showAssistant ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowAssistant(true);
          }}
          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 px-4 rounded-md transition-colors text-sm"
        >
          ✨ Chiedi all'assistente AI
        </button>
      ) : (
        <GeminiAssistant poi={poi} />
      )}
    </div>
  );
};
